import axios from "axios";

// Same-origin by default: the backend serves this build and mounts the API under /api.
// Override at build time with REACT_APP_API_URL if the frontend is ever hosted separately.
const API_BASE_URL = process.env.REACT_APP_API_URL || "/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// A 401 from most endpoints means the session token itself is invalid/
// expired, so force a re-login. But /auth/login and /auth/change-password
// also legitimately return 401 for "wrong password" while the current
// session is still perfectly valid -- auto-logging out there would kick
// the user out just for mistyping their current password while changing
// it, which is exactly what happened testing this.
const EXPECTED_401_PATHS = ["/auth/login", "/auth/change-password"];

// Handle 401 errors, and pick up a sliding-session token refresh
api.interceptors.response.use(
  (response) => {
    // Backend reissues the token once the current one is more than a day
    // old (see backend/middleware/auth.js) so an actively-used session
    // never hits its fixed expiry -- only real inactivity does. Swap it in
    // transparently; nothing else needs to know this happened.
    // Only while still logged in: a request that was in flight when the user
    // hit Logout can land afterwards and would otherwise write a token back
    // into localStorage (user removed, token left behind).
    const refreshedToken = response.headers["x-refreshed-token"];
    if (refreshedToken && localStorage.getItem("token")) {
      localStorage.setItem("token", refreshedToken);
    }
    return response;
  },
  (error) => {
    const url = error.config?.url || "";
    const isExpected401 = EXPECTED_401_PATHS.some((path) => url.includes(path));
    if (error.response?.status === 401 && !isExpected401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);

// Auth API
export const authAPI = {
  login: (data) => api.post("/auth/login", data),
  register: (data) => api.post("/auth/register", data),
  getProfile: () => api.get("/auth/profile"),
  updateProfile: (data) => api.put("/auth/profile", data),
  changePassword: (data) => api.put("/auth/change-password", data),
};

// Parts API (simplified - includes color, category, supplier)
export const partsAPI = {
  getAll: () => api.get("/parts"),
  getById: (id) => api.get(`/parts/${id}`),
  getLowStock: () => api.get("/parts/low-stock"),
  getColors: () => api.get("/parts/colors"),
  getCategories: () => api.get("/parts/categories"),
  getSuppliers: () => api.get("/parts/suppliers"),
  getByCategory: (category) =>
    api.get(`/parts/category/${encodeURIComponent(category)}`),
  getByColor: (color) => api.get(`/parts/color/${encodeURIComponent(color)}`),
  getBySupplier: (supplier) =>
    api.get(`/parts/supplier/${encodeURIComponent(supplier)}`),
  create: (data) => api.post("/parts", data),
  update: (id, data) => api.put(`/parts/${id}`, data),
  updateQuantity: (id, data) => api.patch(`/parts/${id}/quantity`, data),
  delete: (id) => api.delete(`/parts/${id}`),
};

// Equipment API (simplified - brand/category are columns)
export const equipmentAPI = {
  getAll: () => api.get("/equipment"),
  getById: (id) => api.get(`/equipment/${id}`),
  getBrands: () => api.get("/equipment/brands"),
  getCategories: () => api.get("/equipment/categories"),
  create: (data) => api.post("/equipment", data),
  update: (id, data) => api.put(`/equipment/${id}`, data),
  delete: (id) => api.delete(`/equipment/${id}`),
  addPart: (equipmentId, data) =>
    api.post(`/equipment/${equipmentId}/parts`, data),
  removePart: (equipmentId, partId) =>
    api.delete(`/equipment/${equipmentId}/parts/${partId}`),
  produce: (equipmentId) => api.post(`/equipment/${equipmentId}/produce`),
};

// Equipment Templates API
export const equipmentTemplatesAPI = {
  getAll: () => api.get("/equipment-templates"),
  getById: (id) => api.get(`/equipment-templates/${id}`),
  create: (data) => api.post("/equipment-templates", data),
  createFromEquipment: (data) =>
    api.post("/equipment-templates/from-equipment", data),
  createEquipment: (templateId, data) =>
    api.post(`/equipment-templates/${templateId}/create-equipment`, data),
  update: (id, data) => api.put(`/equipment-templates/${id}`, data),
  delete: (id) => api.delete(`/equipment-templates/${id}`),
  getComponentsStock: () => api.get("/equipment-templates/components/stock"),
  setComponentReadyStock: (templateId, quantity) =>
    api.put(`/equipment-templates/${templateId}/components-stock`, { quantity }),
  getComponentStockMovements: (templateId) =>
    api.get(`/equipment-templates/${templateId}/components-stock/movements`),
};

// Orders API
export const ordersAPI = {
  getAll: () => api.get("/orders"),
  getById: (id) => api.get(`/orders/${id}`),
  getStats: () => api.get("/orders/stats"),
  getPartsSummary: () => api.get("/orders/parts-summary"),
  getBackorderParts: () => api.get("/orders/backorder-parts"),
  create: (data) => api.post("/orders", data),
  updateStatus: (id, data) => api.put(`/orders/${id}/status`, data),
  complete: (id, data) => api.put(`/orders/${id}/complete`, data),
  update: (id, data) => api.patch(`/orders/${id}`, data), // ← only keep this one
  delete: (id) => api.delete(`/orders/${id}`),
  addItem: (orderId, itemData) =>
    api.post(`/orders/${orderId}/items`, itemData),
  updateItem: (itemId, data) => api.patch(`/orders/item/${itemId}`, data),
  deleteItem: (itemId) => api.delete(`/orders/item/${itemId}`),
  transferItem: (itemId, data) =>
    api.post(`/orders/item/${itemId}/transfer`, data),
};

// Stock API
export const stockAPI = {
  getMovements: () => api.get("/stock/movements"),
  getMovementsByPart: (partId) => api.get(`/stock/movements/${partId}`),
  getLevels: () => api.get("/stock/levels"),
  getAlerts: () => api.get("/stock/alerts"),
  getSummary: () => api.get("/stock/summary"),
  addStock: (data) => api.post("/stock/add", data),
  adjustStock: (data) => api.post("/stock/adjust", data),
};

// Users API
export const usersAPI = {
  getAll: () => api.get("/users"),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post("/users", data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// "To produce" notes + navbar notifications
export const productionTasksAPI = {
  getAll: (scope) => api.get("/production-tasks", { params: { scope } }),
  getNotifications: () => api.get("/production-tasks/notifications"),
  create: (data) => api.post("/production-tasks", data),
  updateStatus: (id, status) => api.patch(`/production-tasks/${id}/status`, { status }),
  markRead: (id) => api.patch(`/production-tasks/${id}/read`),
  getComments: (id) => api.get(`/production-tasks/${id}/comments`),
  addComment: (id, body) => api.post(`/production-tasks/${id}/comments`, { body }),
  markAllRead: () => api.post("/production-tasks/read-all"),
  delete: (id) => api.delete(`/production-tasks/${id}`),
};

// Vacation / absence calendar
export const vacationsAPI = {
  getAll: (from, to) => api.get("/vacations", { params: { from, to } }),
  getSummary: (year) => api.get("/vacations/summary", { params: { year } }),
  create: (data) => api.post("/vacations", data),
  update: (id, data) => api.put(`/vacations/${id}`, data),
  delete: (id) => api.delete(`/vacations/${id}`),
  setAllowance: (userId, days) => api.put(`/vacations/allowance/${userId}`, { days }),
  getUserStats: (userId, year) => api.get(`/vacations/user/${userId}`, { params: { year } }),
  getHolidays: (from, to) => api.get("/vacations/holidays", { params: { from, to } }),
  addHolidays: (holidays) => api.post("/vacations/holidays", { holidays }),
  deleteHoliday: (id) => api.delete(`/vacations/holidays/${id}`),
  getOvertime: (from, to) => api.get("/vacations/overtime", { params: { from, to } }),
  createOvertime: (data) => api.post("/vacations/overtime", data),
  updateOvertime: (id, data) => api.put(`/vacations/overtime/${id}`, data),
  deleteOvertime: (id) => api.delete(`/vacations/overtime/${id}`),
};

// Customers API
export const customersAPI = {
  getAll: () => api.get("/customers"),
  getById: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post("/customers", data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
};

// Dealers API
export const dealersAPI = {
  getAll: () => api.get("/dealers"),
  getById: (id) => api.get(`/dealers/${id}`),
  create: (data) => api.post("/dealers", data),
  update: (id, data) => api.put(`/dealers/${id}`, data),
  sync: (id) => api.post(`/dealers/${id}/sync`),
  delete: (id) => api.delete(`/dealers/${id}`),
};

// Devices API (serial-number tracking)
export const devicesAPI = {
  getAll: () => api.get("/devices"),
  getById: (id) => api.get(`/devices/${id}`),
  getBySerial: (serial) => api.get(`/devices/by-serial/${encodeURIComponent(serial)}`),
  getRepairs: (id) => api.get(`/devices/${id}/repairs`),
  create: (data) => api.post("/devices", data),
  update: (id, data) => api.put(`/devices/${id}`, data),
  delete: (id) => api.delete(`/devices/${id}`),
};

// Repairs API
export const repairsAPI = {
  getAll: () => api.get("/repairs"),
  getById: (id) => api.get(`/repairs/${id}`),
  getStats: () => api.get("/repairs/stats"),
  create: (data) => api.post("/repairs", data),
  update: (id, data) => api.put(`/repairs/${id}`, data),
  updateStatus: (id, data) => api.put(`/repairs/${id}/status`, data),
  addDevice: (id, data) => api.post(`/repairs/${id}/devices`, data),
  removeDevice: (id, deviceId) => api.delete(`/repairs/${id}/devices/${deviceId}`),
  addAssignee: (id, data) => api.post(`/repairs/${id}/assignees`, data),
  removeAssignee: (id, userId) => api.delete(`/repairs/${id}/assignees/${userId}`),
  addPart: (id, data) => api.post(`/repairs/${id}/parts`, data),
  removePart: (id, partsUsedId) => api.delete(`/repairs/${id}/parts/${partsUsedId}`),
  // Plain URL, not an axios call -- window.open() can't carry the Authorization
  // header the axios interceptor injects, so the token is passed as a query
  // param instead (see backend/middleware/auth.js).
  getDocumentUrl: (id, focus) =>
    `${API_BASE_URL}/repairs/${id}/document?focus=${focus}&token=${encodeURIComponent(localStorage.getItem("token") || "")}`,
  getShiplabel: (id) => api.get(`/repairs/${id}/shiplabel`),
  delete: (id) => api.delete(`/repairs/${id}`),
  getAttachments: (id) => api.get(`/repairs/${id}/attachments`),
  // Content-Type must be cleared, not just left default -- the axios
  // instance's default "application/json" header would otherwise override
  // the multipart boundary the browser needs to set for FormData bodies,
  // and multer can't parse a multipart body without its own boundary.
  uploadAttachments: (id, formData) =>
    api.post(`/repairs/${id}/attachments`, formData, { headers: { "Content-Type": undefined } }),
  deleteAttachment: (id, attachmentId) => api.delete(`/repairs/${id}/attachments/${attachmentId}`),
  // Plain URL, same reasoning as getDocumentUrl -- opened via window.open()/<a>, not axios.
  getAttachmentUrl: (id, attachmentId) =>
    `${API_BASE_URL}/repairs/${id}/attachments/${attachmentId}/download?token=${encodeURIComponent(localStorage.getItem("token") || "")}`,
};

export default api;
